import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { supabase } from './supabase';
import './JoinForm.css';

const CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_DISCORD_REDIRECT_URI;
const SCOPE = process.env.REACT_APP_DISCORD_SCOPE;

const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;

const JoinForm = () => {
    const [formData, setFormData] = useState({
        username: '',
        gamertag: '',
        server: '',
        playtime: '',
        age: '',
        voice: '',
        role: '',
        customRole: '',
        avatarUrl: '',
    });

    const [discordUser, setDiscordUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [hasSubmittedBefore, setHasSubmittedBefore] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem('discord_user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            const avatarUrl = user.avatar 
            ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
            : `https://cdn.discordapp.com/embed/avatars/${Number(user.discriminator) % 5}.png`;
            setDiscordUser(user);
            setFormData(prev => ({
                ...prev,
                id: user.id,
                username: `${user.username}#${user.discriminator}`,
                avatarUrl: avatarUrl,
            }));

            checkPreviousSubmission(user.id);
        }
    }, []);

    const checkPreviousSubmission = async userId => {
        try {
            const { data, error } = await supabase
                .from('form_submissions')
                .select('*')
                .eq('discord_user_id', userId)
                .single();

            if (error) {
                console.error('Error checking submission:', error);
                return;
            }

            if (data) {
                setHasSubmittedBefore(true);
                setIsSubmitted(true);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleDiscordLogin = () => {
        localStorage.setItem('lastVisitedUrl', window.location.pathname);
        const authUrl = `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
            REDIRECT_URI
        )}&response_type=code&scope=${encodeURIComponent(SCOPE)}`;
        window.location.href = authUrl;
    };

    const handleChange = e => {
        const { name, value } = e.target;

        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const saveToSupabase = async formData => {
        try {
            const { error } = await supabase.from('form_submissions').insert([
                {
                    discord_user_id: formData.id,
                    discord_username: formData.username,
                    gamertag: formData.gamertag,
                },
            ]);

            if (error) {
                throw error;
            }

            return true;
        } catch (error) {
            console.error('Error saving to Supabase:', error);
            return false;
        }
    };

    const sendToDiscord = async formData => {
        const roleValue =
            formData.role === 'Other' ? `Other: ${formData.customRole}` : formData.role;
            
            const voiceValue = {
            'iya': 'Bisa',
            'tidak': 'Tidak bisa',
            'pake nanya': 'PAKE NANYA🧏🏼'
        }[formData.voice] || formData.voice;
        

        const embed = {
            title: '📝 rekruitmen',
            color: 0x7289da,
            fields: [
                {
                    name: 'Username',
                    value: formData.username,
                    inline: true,
                },
                {
                    name: 'User ID',
                    value: `[${formData.id}](https://discordapp.com/users/${formData.id})`,
                    inline: true,
                },
                {
                    name: 'Gamertag Minecraft',
                    value: formData.gamertag,
                    inline: true,
                },
                {
                    name: 'Server yang dia mainkan',
                    value: formData.server || 'Tidak disebutkan',
                    inline: true,
                },
                {
                    name: 'Sejak kapan dia bermain di server pn',
                    value: formData.playtime,
                    inline: true,
                },
                {
                    name: 'Umur dia',
                    value: formData.age.toString(),
                    inline: true,
                },
                {
                    name: 'Apakah dia bisa diajak ke voice di Discord',
                    value: voiceValue,
                    inline: true,
                },
                {
                    name: 'Role yang dia miliki',
                    value: roleValue,
                    inline: true,
                },
            ],
            timestamp: new Date().toISOString(),
            thumbnail: {
                    url: formData.avatarUrl
                },
            footer: {
                text: 'Sistem Rekruitmen TFH'
            }
        };

        const webhookBody = {
            embeds: [embed],
        };

        try {
            const response = await fetch(DISCORD_WEBHOOK_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(webhookBody),
            });

            if (!response.ok) {
                throw new Error('Failed to send to Discord');
            }

            return true;
        } catch (error) {
            console.error('Error sending to Discord:', error);
            return false;
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!discordUser) {
            alert('Silakan login dengan Discord terlebih dahulu!');
            return;
        }

        // Check if user has already submitted
        const { data } = await supabase
            .from('form_submissions')
            .select('*')
            .eq('discord_user_id', formData.id)
            .single();

        if (data) {
            setIsSubmitted(true);
            setHasSubmittedBefore(true);
            return;
        }

        setIsSubmitting(true);

        try {
            // Save to Supabase first
            const savedToSupabase = await saveToSupabase(formData);
            if (!savedToSupabase) {
                throw new Error('Failed to save to database');
            }

            // Then send to Discord
            const success = await sendToDiscord(formData);

            if (success) {
                setIsSubmitted(true);
                setFormData({
                    username: formData.username,
                    gamertag: '',
                    role: '',
                });
            } else {
                // If Discord webhook fails, we should probably remove the Supabase entry
                await supabase.from('form_submissions').delete().eq('discord_user_id', formData.id);

                alert('Gagal mengirim formulir. Silakan coba lagi.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Terjadi eror saat mengirim formulir.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Komponen Halaman Terima Kasih
    const ThankYouPage = () => (
        <div className="thank-you-container">
            <h2 className="thank-you-title">Terima Kasih!</h2>
            <p className="thank-you-message">
                {hasSubmittedBefore
                    ? 'Anda sudah pernah mengisi formulir sebelumnya.'
                    : 'Terima kasih telah berkenan mengisi formulir untuk bergabung dengan keluarga. Kami akan menghubungi Anda lagi di Discord jika Anda diterima.'}
            </p>
            <p className="thank-you-icon">❤️</p>
        </div>
    );

    // Jika form sudah terkirim atau user sudah pernah mengisi, tampilkan halaman terima kasih
    if (isSubmitted || hasSubmittedBefore) {
        return <ThankYouPage />;
    }

    return (
        <div className="form-container">
            <div className="join-form">
                <h2 className="form-title">Formulir Pendaftaran</h2>

                {!discordUser && (
                    <div className="discord-auth-section">
                        <button
                            type="button"
                            className="discord-login-button"
                            onClick={handleDiscordLogin}
                        >
                            <FontAwesomeIcon icon={faDiscord} className="discord-icon" />
                            Login dengan Discord
                        </button>
                    </div>
                )}

                {discordUser ? (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Username Discord</label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                disabled
                                className="disabled-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Gamertag Minecraft kamu</label>
                            <span className="example-text">*Contoh: Beduu24</span>
                            <input
                                type="text"
                                name="gamertag"
                                value={formData.gamertag}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Di server mana kamu sering bermain</label>
                            <select
                                name="server"
                                value={formData.server}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Pilih server yang kamu mainkan</option>
                                <option value="Vanilla">Vanilla</option>
                                <option value="Ekonomi">Ekonomi</option>
                                <option value="Acid Island">Acid Island</option>
                                <option value="Skyblock">Skyblock</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Sejak kapan kamu bermain di server Prownetwork?</label>
                            <span className="example-text">*Contoh: Sekitar satu tahun, bang</span>
                            <input
                                type="text"
                                name="playtime"
                                value={formData.playtime}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Berapa umur kamu?</label>
                            <input
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                min="1"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Apakah kamu bisa diajak ke voice di Discord?</label>
                            <select
                                name="voice"
                                value={formData.voice}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Pilih jawaban</option>
                                <option value="iya">Iya, bisa</option>
                                <option value="tidak">Tidak bisa</option>
                                <option value="pake nanya">PAKE NANYA🧏🏼</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Apa role yang kamu miliki?</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Pilih role kamu</option>
                                <option value="Builder">Builder</option>
                                <option value="Miner">Miner</option>
                                <option value="Redstone"> Redstone </option>
                                <option value="Other">other</option>
                            </select>

                            {formData.role === 'Other' && (
                                <input
                                    type="text"
                                    name="customRole"
                                    value={formData.customRole}
                                    onChange={handleChange}
                                    placeholder="Tuliskan role yang kamu miliki"
                                    className="mt-2"
                                    required
                                />
                            )}
                        </div>
                        <button type="submit" className="submit-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Mengirim...' : 'Kirim'}
                        </button>
                    </form>
                ) : (
                    <div className="login-required-message">
                        Silakan login dengan Discord untuk mengakses formulir pendaftaran
                    </div>
                )}
            </div>
        </div>
    );
};

export default JoinForm;
